import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { debounce } from "capitalrx-components-2.0";
import { isZipCode } from "capitalrx-components-2.0/lib/components/FormElements/validation";
import { Checkbox, Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { DataId, Filterable, Icon, Panel, useAlert, useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";
import { DateTime } from "luxon";

import { getGeolocation } from "@/api/axios/geolocation";
import { Drug } from "@/api/types";
import { TypeaheadTextbox } from "@/common/components/TypeaheadTextbox";
import { TypeaheadTextboxDatasourceRow } from "@/common/components/TypeaheadTextbox/Props";
import { useNavigateSearch, useSelectedEnrollmentPlan } from "@/premium/hooks";
import { DrugDetailsForm } from "@/premium/pages/FindPharmacies/DrugDetailsForm";
import { ThemedSubmitButton } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";
import { useIsMedicarePartD } from "@/premium/hooks/useHasMedicarePlans";

interface FindPharmaciesForm {
  readonly useMyLocation: boolean;
  readonly zipCode: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly pharmacyName?: string;
  readonly drug?: {
    readonly drugQuantity: string;
    readonly drugDisplayQuantity: string;
    readonly drugDosage: string;
    readonly drugForm: string;
    readonly claimRunDate?: string;
    readonly daysSupply: string;
  };
  readonly member?: {
    readonly gender?: string;
    readonly age?: string;
  };
}

const drugDebounceFilter = debounce((dataSource: Filterable, value: string | null) => {
  if (!value) {
    dataSource.clearFilter();
  } else {
    dataSource.filterBy("search", value);
  }
}, 300);

const translationPath = "PLAN_DETAILS.pharmacyPricingPanel";

export const PharmaciesAndPricingPanel = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateSearch();
  const [searchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");
  const { enrollmentPlanCsid, hideDrugPricing, hidePharmacySearch } = useSelectedEnrollmentPlan();
  const [selectedDrug, setSelectedDrug] = useState<Drug>(undefined);
  const [drugName, setDrugName] = useState<string>("");
  const isMedicarePlan = useIsMedicarePartD(enrollmentPlanCsid?.id);
  const alert = useAlert();

  useEffect(() => {
    if (hidePharmacySearch) {
      navigate("/");
    }
  }, [hidePharmacySearch]);

  const defaultValues: FindPharmaciesForm = useMemo(() => {
    return {
      useMyLocation: false,
      zipCode: "",
    };
  }, []);

  const findPharmaciesForm = useForm<FindPharmaciesForm>({
    defaultValues: defaultValues,
    shouldFocusError: false,
  });

  const useMyLocation = findPharmaciesForm.watch("useMyLocation");

  useEffect(() => {
    if (useMyLocation) {
      findPharmaciesForm.setValue("zipCode", "");
      navigator.geolocation.getCurrentPosition(
        (position) => {
          findPharmaciesForm.setValue("latitude", position.coords.latitude);
          findPharmaciesForm.setValue("longitude", position.coords.longitude);
        },
        () => {
          alert({
            message: translate("FIND_PHARMACIES.findPharmacies.cannotGetCurrentLocation"),
            type: "error",
            style: "toast",
          });
        },
      );
    }
  }, [alert, useMyLocation]);

  const drugSearchDataSource = useRemoteTableDataSource<{ readonly drug: Drug }, TypeaheadTextboxDatasourceRow<any>>(
    `${site.baseUrl}/drug/list`,
    (row) => {
      return {
        displayText: row.drug.drug_name,
        value: row.drug.id,
        data: row.drug,
      };
    },
    (row) => row.value,
    {
      enabled: (drugName?.length || 0) >= 3,
      refetchInterval: 0,
      onError: () => {},
      authTokenRequired: false,
    },
  );

  useEffect(() => {
    drugDebounceFilter(drugSearchDataSource, drugName);
  }, [drugName]);

  const onDrugSelected = useCallback(
    (drugId: DataId) => {
      const row = drugSearchDataSource.lookup(drugId);
      if (row) {
        setSelectedDrug(row.data);
        const mostCommonDosage = row.data.most_common_dosage ?? row.data.configurations[0].dosage;
        const mostCommonForm = row.data.most_common_form ?? row.data.configurations[0].dosage_form;
        const mostCommonQuantity = row.data.most_common_quantity_dispensed ?? row.data.configurations[0].quantities[0];

        findPharmaciesForm.setValue("drug.drugForm", mostCommonForm);
        findPharmaciesForm.setValue("drug.drugDosage", mostCommonDosage);
        findPharmaciesForm.setValue("drug.drugQuantity", mostCommonQuantity.toString());
        findPharmaciesForm.setValue("drug.daysSupply", "30");

        const defaultRunDate = DateTime.max(DateTime.fromISO(enrollmentPlanCsid.claim_run_date, { zone: "utc" }), DateTime.utc());

        findPharmaciesForm.setValue("drug.claimRunDate", defaultRunDate.toFormat("MM/dd/yyyy"));

        findPharmaciesForm.setValue("member.gender", "Female");
        findPharmaciesForm.setValue("member.age", "44");
      }
    },
    [drugSearchDataSource, findPharmaciesForm],
  );

  const tryGeoLocateWithZipCode = async (zipCode: string) => {
    try {
      const location = await getGeolocation(zipCode);
      if (location?.latitude && location?.longitude) {
        return location;
      } else {
        return undefined;
      }
    } catch {
      return undefined;
    }
  };

  const onFindPharmacies = useCallback(
    async (formData: FindPharmaciesForm) => {
      let params: { [key: string]: string } = { enrollmentPlan: enrollmentPlanId };
      if (formData.pharmacyName) {
        params = { ...params, pharmacyName: formData.pharmacyName };
      }
      if (selectedDrug) {
        // figure out the real quantity
        const drugForm = formData.drug.drugForm;
        const drugDosage = formData.drug.drugDosage;
        const drugQuantityWithForm = formData.drug.drugQuantity;

        const drugConfiguration = selectedDrug.configurations.find((configuration) => {
          return configuration.dosage_form === drugForm && configuration.dosage === drugDosage;
        });
        const rawAmount = parseFloat(drugQuantityWithForm);
        const displayQuantityIdx = drugConfiguration.display_quantities.indexOf(String(rawAmount));
        const quantity = drugConfiguration.quantities[displayQuantityIdx] || rawAmount;

        // set the claim run date
        let claimRunDate = DateTime.fromISO(enrollmentPlanCsid.claim_run_date);
        if (formData.drug.claimRunDate) {
          claimRunDate = DateTime.fromFormat(formData.drug.claimRunDate, "MM/dd/yyyy").toUTC();
        }

        params = {
          ...params,
          age: formData.member?.age || "44",
          gender: formData.member?.gender || "Female",
          daysSupply: encodeURIComponent(formData.drug?.daysSupply || "30"),
          drugId: selectedDrug.id.toString(),
          dosageForm: encodeURIComponent(drugForm),
          dosage: encodeURIComponent(drugDosage),
          quantity: quantity.toString(),
          claimRunDate: claimRunDate.toISO(),
        };
      }
      let locationParams = null;
      if (!formData.useMyLocation) {
        const loc = await tryGeoLocateWithZipCode(formData.zipCode);
        if (loc) {
          locationParams = { latitude: loc.latitude, longitude: loc.longitude, zipCode: formData.zipCode };
        }
      } else if (formData.latitude && formData.longitude) {
        locationParams = { ...params, latitude: formData.latitude.toString(), longitude: formData.longitude.toString() };
      }

      if (locationParams) {
        params = { ...params, ...locationParams };
        logEvent(ANALYTIC_EVENTS.FIND_PHARMACY);
        navigate("/plans/plan-details/pharmacies", params);
      } else {
        alert({
          message: `${translate("FIND_PHARMACIES.findPharmacies.unableToFindLocation")} ${formData.zipCode}.`,
          style: "toast",
          type: "error",
        });
      }
    },
    [alert, selectedDrug, useMyLocation],
  );
  const zipOrCurrentLocationValidator = useCallback(
    (value: string) => {
      return useMyLocation || isZipCode(value);
    },
    [useMyLocation],
  );

  return (
    <Panel>
      <Stack>
        <Heading>{translate(`${translationPath}.findPharmaciesAndPricing`)}</Heading>
        <Body>{translate(`${translationPath}.searchForInfo`)}</Body>
        <Form form={findPharmaciesForm} onSubmit={onFindPharmacies}>
          <Stack>
            <Stack>
              <Stack>
                <Stack spacing="none">
                  <Textbox.Field
                    testId="zip-code-field"
                    name="zipCode"
                    disabled={useMyLocation}
                    required
                    prefix={<Icon name="search" />}
                    label={translate("FIND_PHARMACIES.findPharmacies.enterZipCode")}
                    validator={zipOrCurrentLocationValidator}
                  />
                  <Checkbox.Field name="useMyLocation" label={translate("FIND_PHARMACIES.findPharmacies.useMyCurrentLocation")} />
                </Stack>
                <Textbox.Field
                  name="pharmacyName"
                  testId="pharmacy-name-field"
                  prefix={<Icon name="search" />}
                  label={translate(`${translationPath}.pharmacyName`)}
                />
              </Stack>
              {!hideDrugPricing && !isMedicarePlan && (
                <Stack>
                  <Stack spacing="none">
                    <TypeaheadTextbox
                      label={translate(`${translationPath}.drugName`)}
                      prefix={<Icon name="search" />}
                      shouldOpen={(currentValue) => {
                        return currentValue.length >= 3;
                      }}
                      dataSource={drugSearchDataSource}
                      onItemSelected={onDrugSelected}
                      testId="find-drug-pricing-field"
                      onChange={(newDrugName) => {
                        // clear any previous drop-down selection when input text changes
                        setSelectedDrug(undefined);
                        setDrugName(newDrugName);
                      }}
                      value={selectedDrug?.drug_name || drugName}
                      instructions={translate(`${translationPath}.threeCharMin`)}
                    />
                  </Stack>
                </Stack>
              )}
              {selectedDrug && !hideDrugPricing && !isMedicarePlan && <DrugDetailsForm drug={selectedDrug} />}
            </Stack>
            <Shelf spacing="small">
              <ThemedSubmitButton testId="find-pharmacy-btn">{translate(`${translationPath}.search`)}</ThemedSubmitButton>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        </Form>
      </Stack>
    </Panel>
  );
};
